/** 统一工作空间 API。所有路径集中在 config/api-paths.ts。 */

import { request } from '@/api/instance'
import { API_PATHS } from '@/config/api-paths'
import type { ApiResponse, PaginationResponse } from '@/types/response'
import type {
  AuthorizationContextPayload,
  RegistrationWorkspaceOption,
  StudentTransferApplication,
  StudentTransferApplicationContext,
  StudentTransferApplicationCreateParams,
  StudentTransferApplicationListParams,
  WorkspaceInvitationRegistrationParams,
  WorkspaceOnboardingSelectParams,
  WorkspaceInvitationPreview,
  WorkspaceInvitationRegistrationResult,
} from '@/types/workspace'

/** 获取当前账号的用户、唯一 Workspace、角色和权限快照。 */
export function getAuthorizationContext() {
  return request.get<ApiResponse<AuthorizationContextPayload>>(API_PATHS.auth.context, {
    enableDedup: false,
  })
}

/** 获取公开邀请信息，用于注册链接注册页展示。 */
export function getInvitationPreview(invitationToken: string) {
  return request.get<ApiResponse<WorkspaceInvitationPreview>>(API_PATHS.invitation.preview, {
    params: { invitation_token: invitationToken },
    skipAuth: true,
    skipTokenRefresh: true,
  })
}

/** 通过范围化邀请注册新的学习成员账号。 */
export function registerWithInvitation(params: WorkspaceInvitationRegistrationParams) {
  return request.post<ApiResponse<WorkspaceInvitationRegistrationResult>>(
    API_PATHS.invitation.register,
    params,
    {
      skipAuth: true,
      skipTokenRefresh: true,
    }
  )
}

/** 搜索尚未归属工作空间的账号可申请加入的工作空间。 */
export function getRegistrationOptions(keyword?: string) {
  return request.get<ApiResponse<{ items: RegistrationWorkspaceOption[] }>>(
    API_PATHS.workspace.registrationOptions,
    {
      skipAuth: true,
      skipTokenRefresh: true,
      params: keyword ? { keyword } : undefined,
    }
  )
}

/** 已登录但没有成员关系的账号选择目标工作空间。 */
export function selectOnboardingWorkspace(params: WorkspaceOnboardingSelectParams) {
  return request.post<ApiResponse<AuthorizationContextPayload>>(
    API_PATHS.workspace.onboardingSelect,
    params
  )
}

/** 取消尚未审核的工作空间加入申请，并返回最新访问决策。 */
export function cancelJoinApplication(applicationId: number) {
  return request.post<ApiResponse<AuthorizationContextPayload>>(
    API_PATHS.workspace.joinApplicationCancel(applicationId)
  )
}

/** 获取班主任可提交转班申请的学生与目标班级范围。 */
export function getTransferApplicationContext() {
  return request.get<ApiResponse<StudentTransferApplicationContext>>(
    API_PATHS.workspace.transferApplicationContext
  )
}

/** 获取当前班主任提交的转班申请。 */
export function getTransferApplications(params?: StudentTransferApplicationListParams) {
  return request.get<ApiResponse<PaginationResponse<StudentTransferApplication>>>(
    API_PATHS.workspace.transferApplications,
    { params }
  )
}

/** 为本班学生提交转班申请。 */
export function createTransferApplication(params: StudentTransferApplicationCreateParams) {
  return request.post<ApiResponse<StudentTransferApplication>>(
    API_PATHS.workspace.transferApplications,
    params
  )
}

/** 取消仍在等待审批的转班申请。 */
export function cancelTransferApplication(applicationId: number) {
  return request.post<ApiResponse<StudentTransferApplication>>(
    API_PATHS.workspace.transferApplicationCancel(applicationId)
  )
}
